import { array, string, bool, func, oneOfType, node } from 'prop-types';
import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import history from 'store/history';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { get } from 'ravenjs/utils/lodash';

import Acl from 'modules/acl';
import { APP_PERMISSIONS } from 'constants/permissions';
import { MODALS } from 'constants/modals';
import { actions as uiActions } from 'modules/ui';
import { IMPERSONATION_RESTRICTED_SUB_NAV_ITEMS } from 'constants/navigation';
import {
    ROOT_PUBLIC_ROUTE,
    ROOT_PRIVATE_ROUTE,
    ROOT_PRIVATE_ROUTE_PLATFORM_LITE,
} from 'constants/routes';
import { selectors as authSelectors } from 'modules/auth';
import { getLoginRedirectSearchString } from 'utils/search';
import MESSAGES from 'constants/messages';
import { isSoftLoginRoute } from 'utils/thrservice';

/**
 * A guard component that wraps route content with auth and permission checks.
 * Replaces the v5 custom Route wrapper for react-router v6.
 *
 * @method      AuthRoute
 * @param       {Object} props The passed in props
 * @constructor
 */
function AuthRoute(props) {
    const {
        children,
        showPermissionModal,
        disabled,
        closeModal,
        failedPermissionsRedirect,
        isImpersonatingUser,
        isLoggedIn,
        isPublic,
        path,
        permissions,
        preventAllPermissionCheck,
        openModal,
    } = props;

    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const navigateRef = React.useRef(navigate);
    const locationRef = React.useRef(location);
    navigateRef.current = navigate;
    locationRef.current = location;

    const historyShim = React.useMemo(
        () => ({
            push: (p, state) => {
                if (typeof p === 'object') {
                    navigateRef.current(p, { state: p.state || state });
                } else {
                    navigateRef.current(p, { state });
                }
            },
            replace: (p, state) => {
                if (typeof p === 'object') {
                    navigateRef.current(p, { replace: true, state: p.state || state });
                } else {
                    navigateRef.current(p, { replace: true, state });
                }
            },
            go: n => navigateRef.current(n),
            goBack: () => navigateRef.current(-1),
            goForward: () => navigateRef.current(1),
            get action() {
                return history.action;
            },
            get length() {
                return window.history.length;
            },
            listen: history.listen.bind(history),
            block: history.block ? history.block.bind(history) : () => () => {},
            get location() {
                return locationRef.current;
            },
            createHref: history.createHref
                ? history.createHref.bind(history)
                : to =>
                      typeof to === 'string'
                          ? to
                          : `${to.pathname || ''}${to.search || ''}${to.hash || ''}`,
        }),
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const match = React.useMemo(
        () => ({
            params,
            path: location.pathname,
            url: location.pathname,
        }),
        [params, location.pathname]
    );

    const renderChildren = () =>
        React.isValidElement(children)
            ? React.cloneElement(children, { history: historyShim, location, match, navigate })
            : children;

    const redirectSearch = getLoginRedirectSearchString(location);
    const route_url = Acl.check(APP_PERMISSIONS.platformliteCompanyDashboardView)
        ? ROOT_PRIVATE_ROUTE_PLATFORM_LITE
        : ROOT_PRIVATE_ROUTE;
    const queryParams = queryString.parse(location.search);

    if (!isLoggedIn && !isSoftLoginRoute(location.pathname)) {
        const partnerLoginUrl = get(queryParams, 'partnerLoginUrl', null);
        if (partnerLoginUrl) {
            window.location.href = partnerLoginUrl;
            return null;
        }
    }

    if (isPublic) {
        return renderChildren();
    }

    if (!isLoggedIn) {
        const loginQueryParams = queryString.parse(location.search);
        const partnerLoginUrl = get(loginQueryParams, 'partnerLoginUrl', null);

        if (partnerLoginUrl) {
            window.location.href = partnerLoginUrl;
            return null;
        }

        const redirectTo = {
            pathname: ROOT_PUBLIC_ROUTE,
            search: redirectSearch,
        };

        return <Navigate to={redirectTo} replace />;
    }

    if (isImpersonatingUser && path && IMPERSONATION_RESTRICTED_SUB_NAV_ITEMS.includes(path)) {
        const redirectTo = {
            pathname: failedPermissionsRedirect || route_url,
            search: redirectSearch,
        };

        return <Navigate to={redirectTo} replace />;
    }

    if (!_isEmpty(permissions) || disabled) {
        const allowed = Acl.check(permissions, !preventAllPermissionCheck);
        const redirectTo = {
            pathname: failedPermissionsRedirect || route_url,
            search: redirectSearch,
        };

        if (showPermissionModal && !allowed) {
            openModal(MODALS.CONFIRM, {
                description: MESSAGES.NO_PERMISSION_MESSAGE,
                actions: [
                    {
                        text: MESSAGES.MODAL.CONFIRM.ACTIONS.OK,
                        color: 'primary',
                        onClick() {
                            closeModal(MODALS.CONFIRM);
                        },
                    },
                ],
            });
        }
        return allowed && !disabled ? renderChildren() : <Navigate to={redirectTo} replace />;
    }

    return renderChildren();
}

AuthRoute.propTypes = {
    children: node.isRequired,
    disabled: bool,
    failedPermissionsRedirect: string,
    isImpersonatingUser: bool,
    isLoggedIn: bool,
    isPublic: bool,
    path: string,
    permissions: oneOfType([array, string]),
    preventAllPermissionCheck: bool,
    openModal: func.isRequired,
    closeModal: func.isRequired,
    showPermissionModal: bool,
};

AuthRoute.defaultProps = {
    disabled: false,
    showPermissionModal: false,
    failedPermissionsRedirect: null,
    isImpersonatingUser: false,
    isLoggedIn: false,
    isPublic: false,
    path: null,
    permissions: null,
    preventAllPermissionCheck: false,
};

const mapStateToProps = createStructuredSelector({
    isImpersonatingUser: authSelectors.isImpersonatingUser,
    isLoggedIn: authSelectors.isUserLoggedIn,
});

const mapDispatchToProps = {
    openModal: uiActions.openModal,
    closeModal: uiActions.closeModal,
};

export { AuthRoute as UnwrappedAuthRoute };
export default connect(mapStateToProps, mapDispatchToProps)(AuthRoute);
